import styled, { useTheme } from "styled-components";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { observer, inject } from "mobx-react";
import { Text } from "../UI";
import CoralisLogo from "../asserts/img/CoralisLogo.svg";
import supportCallImg from "../asserts/img/supportCallMobile.svg";
import reportDetailsImg from "../asserts/img/ClockMobile.svg";
import logoutImg from "../asserts/img/logout.svg";
import tasksImg from "../asserts/img/tasksMobile.svg";
import monthReportsImg from "../asserts/img/monthReportsMobile.svg";
import customersImg from "../asserts/img/clientConnectionsMobile.svg";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import LoadingContainer from "./LoadingContainer";
import ChangeLanguage from "../components/ChangeLanguage";
import { useMobile } from "../utils/mobile";
import { useEffect, useState } from "react";
import closeMsgImg from "../asserts/img/closeX.svg";
import { subscribe, unsubscribe } from "../utils/events";

const drawerWidth = 240;

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 0,
  fontSize: "16px",
  padding: "9px",
  width: "37px",
  height: "37px",
  alignItems: "center",
  display: "flex",
  margin: "8px 0",
  border: "none",
  backgroundColor: "transparent",
}));

const StyledListItemText = styled(ListItemText)(() => ({
  textAlign: "start",
  marginInlineStart: "15px",
  fontSize: "16px",
  fontFamily: "Heebo",

  span: {
    lineHeight: "22px",
  },
}));

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ open, isMobile }) => ({
    flexGrow: 1,
    paddingInlineStart: !isMobile && (!open ? 0 : drawerWidth),
  })
);

const StyledListItem = styled(ListItem)(({ selected }) => ({
  backgroundImage: selected
    ? `linear-gradient( to left, #FF7050, #3B67AD)`
    : `transperant `,
  cursor: "pointer",
  height: 50,
  margin: "12px 0 !important",
}));

const DrawerHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
}));

const AppBar = ({ children, userStore, isLoading }) => {
  const theme = useTheme();
  let location = useLocation().pathname;
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [open, setOpen] = useState(!!isMobile ? false : true);

  useEffect(() => {
    subscribe("showDrawer", () => setOpen(true));
    document.body.style.zoom = "1";
    window.scrollTo(0, 0);
    return () => {
      unsubscribe("showDrear", () => setOpen(false));
    };
  }, []);

  return (
    <Box>
      <CssBaseline />
      <Main open={open} isMobile={isMobile}>
        <LoadingContainer isLoading={isLoading}>{children}</LoadingContainer>
      </Main>
      <Drawer
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            backgroundColor: theme.color.blue,
            color: theme.color.white,
            width: !!isMobile ? "100vw" : drawerWidth,
            border: 0,
          },
        }}
        variant="persistent"
        anchor={i18n.dir() === "rtl" ? "right" : "left"}
        open={open}>
        <DrawerHeader
          isMobile={isMobile}
          style={{
            marginBottom: `2em`,
          }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
            }}>
            <div
              style={{
                backgroundColor: theme.color.white,
                height: 85,
                display: "flex",
                marginBottom: 30,
                width: "100%",
                marginLeft: 0,
                marginRight: 0,
                justifyContent: "flex-start",
                alignItems: "center",
                padding: 20,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              }}>
              <img src={CoralisLogo} alt="logo" style={{ width: 150 }} />
              {isMobile && (
                <img
                  src={closeMsgImg}
                  alt="close"
                  style={{
                    padding: 10,
                    cursor: "pointer",
                    marginInlineStart: isMobile && "auto",
                  }}
                  onClick={() => {
                    setOpen(false);
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "column",
                width: "100%",
                marginInlineStart: 50,
              }}>
              <div>
                <Text
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: theme.color.white,
                  }}>
                  {t("common:wellcome")}
                </Text>
              </div>
              <div style={{ lineHeight: 0.8, fontSize: 18 }}>
                <Text.T4 style={{ color: theme.color.white }}>
                  {userStore.userDetails.FULLNAME}
                </Text.T4>
              </div>
            </div>
          </div>
        </DrawerHeader>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            height: "100vh",
          }}>
          <List style={{ flexGrow: 0, height: "auto" }}>
            <StyledListItem
              button
              selected={location.includes("/support_calls")}
              onClick={() => {
                setOpen(isMobile ? false : true);
                navigate("/support_calls");
              }}>
              <StyledListItemIcon
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                  minWidth: 0,
                }}>
                <img alt="support calls" src={supportCallImg} />
              </StyledListItemIcon>
              <StyledListItemText
                sx={{
                  textAlign: "start",
                  marginInlineStart: "15px",
                  fontSize: "16px",
                }}>
                {t("support_calls:support_calls_title")}
              </StyledListItemText>
            </StyledListItem>
            {userStore.isCoralisEmployee === true && (
              <>
                <StyledListItem
                  button
                  selected={location.includes("/tasks")}
                  onClick={() => {
                    setOpen(isMobile ? false : true);
                    navigate("/tasks");
                  }}>
                  <StyledListItemIcon
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      minWidth: 0,
                    }}>
                    <img alt="tasks" src={tasksImg} />
                  </StyledListItemIcon>
                  <StyledListItemText
                    sx={{
                      textAlign: "start",
                      marginInlineStart: "15px",
                      fontSize: "16px",
                    }}>
                    {t("tasks:tasks_title")}
                  </StyledListItemText>
                </StyledListItem>
                <StyledListItem
                  button
                  selected={location.includes("/report_details")}
                  onClick={() => {
                    setOpen(isMobile ? false : true);
                    navigate("/report_details");
                  }}>
                  <StyledListItemIcon
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      minWidth: 0,
                    }}>
                    <img alt="report details" src={reportDetailsImg} />
                  </StyledListItemIcon>
                  <StyledListItemText
                    sx={{
                      textAlign: "start",
                      marginInlineStart: "15px",
                      fontSize: "16px",
                    }}>
                    {t("report_details:report_details_title")}
                  </StyledListItemText>
                </StyledListItem>
                <StyledListItem
                  button
                  selected={location.includes("/month_reports")}
                  onClick={() => {
                    setOpen(isMobile ? false : true);
                    navigate("/month_reports");
                  }}>
                  <StyledListItemIcon
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      minWidth: 0,
                    }}>
                    <img alt="month reports" src={monthReportsImg} />
                  </StyledListItemIcon>
                  <StyledListItemText
                    sx={{
                      textAlign: "start",
                      marginInlineStart: "15px",
                      fontSize: "16px",
                    }}>
                    {t("month_reports:month_reports_title")}
                  </StyledListItemText>
                </StyledListItem>
                <StyledListItem
                  button
                  selected={location.includes("/customers")}
                  onClick={() => {
                    setOpen(isMobile ? false : true);
                    navigate("/customers");
                  }}>
                  <StyledListItemIcon
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      minWidth: 0,
                    }}>
                    <img alt="customers" src={customersImg} />
                  </StyledListItemIcon>
                  <StyledListItemText
                    sx={{
                      textAlign: "start",
                      marginInlineStart: "15px",
                      fontSize: "16px",
                    }}>
                    {t("customers:customers_title")}
                  </StyledListItemText>
                </StyledListItem>
              </>
            )}
          </List>
        </div>
        <ChangeLanguage />
        <div
          onClick={() => {
            userStore.logout();
            navigate("/login");
          }}
          style={{
            flexDirection: "row",
            borderTop: `1px solid ${theme.color.white}`,
            padding: "1em",
            paddingInlineStart: "1.5em",
            display: "flex",
            alignItems: "center",
            bottom: 0,
            width: "100%",
            cursor: "pointer",
          }}>
          <img
            src={logoutImg}
            alt="logout"
            style={{ marginInlineEnd: "1em" }}
          />
          <Text.H3
            style={{ cursor: "pointer" }}
            color={theme.color.white}
            fontWeight={400}>
            {t("common:logout")}
          </Text.H3>
        </div>
      </Drawer>
    </Box>
  );
};

export default inject("userStore")(observer(AppBar));
